import { api } from '../../../lib/api';
import type {
  SupplierDetail,
  SupplierListItem,
  SupplierListQuery,
  SupplierOption,
  SupplierRequest,
} from './supplierTypes';
import type { PagedResult } from '../../administration/users/userTypes';

function toQueryString(query: SupplierListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Each facet is sent whenever the key is PRESENT, empty string included: an empty value is the
    reader saying "none of these", which matches no row. A truthiness check would drop it and the
    server would read the axis as unmentioned — the three-state facet rule.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.province !== undefined) params.set('province', query.province);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * The suppliers the organization buys from.
 *
 * **Nothing here returns a contact's name.** The write calls carry one and hand it to the
 * identity store; the profile resolves the id it gets back through `personsApi.resolve`.
 */
export const suppliersApi = {
  list: (query: SupplierListQuery, signal?: AbortSignal) =>
    api<PagedResult<SupplierListItem>>(`/api/suppliers${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<SupplierDetail>(`/api/suppliers/${id}`, { signal }),

  /** The live suppliers, for the order form's picker. */
  options: (signal?: AbortSignal) => api<SupplierOption[]>('/api/suppliers/options', { signal }),

  create: (body: SupplierRequest) =>
    api<SupplierDetail>('/api/suppliers', { method: 'POST', body }),

  update: (id: string, body: SupplierRequest) =>
    api<SupplierDetail>(`/api/suppliers/${id}`, { method: 'PUT', body }),

  remove: (id: string) => api<void>(`/api/suppliers/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/suppliers/${id}/restore`, { method: 'POST' }),

  clear: () => api<number>('/api/suppliers/clear-data', { method: 'POST' }),
};
